// @ts-check
// SHOPPING CART store — Zustand (Overmind retirement, Bucket C).
//
// The cart is CLIENT-ONLY and must NEVER appear in SSR HTML: the micro-cache
// (#188) caches rendered HTML shared across every visitor, so a returning
// visitor's cart must not leak into it, and the worker-capacity model depends on
// the region HTML staying identical per visitor. This store therefore renders an
// EMPTY cart during SSR *and* the first client render, then rehydrates from
// localStorage AFTER mount:
//   - `skipHydration: true` — the store does NOT auto-hydrate at module load, so
//     the first client render === the SSR HTML (empty cart) → no #418/#423.
//   - <CartStoreHydrator/> (mounted once in SiteChrome) calls
//     `useCartStore.persist.rehydrate()` in a mount effect; `onRehydrateStorage`
//     flips `_hasHydrated` so any gate can tell hydration has committed.
//
// CART SHAPE — the cart is a plain `{ [itemId]: quantity }` map, IDENTICAL to the
// former Overmind `state.shoppingCart`. There are NO price snapshots in the cart:
// per [[vrs-pricing-per-item-override]] the per-item price snapshot lives on the
// BACKEND Item, and the client resolves prices from the region `shopItems`
// (offers) at render time via findItemById/getShopItemPrice. So this store carries
// only ids+quantities, exactly as before.
//
// PERSISTENCE — a CUSTOM storage adapter reads/writes the RAW map under the SAME
// localStorage key ('shoppingCart') and the SAME shape the Overmind
// updateShoppingCartInLocalStorage effect used (JSON.stringify of the map). This
// means existing in-flight carts survive with NO migration, and a rollback to the
// Overmind cart would still read them. (Zustand's default persist envelope
// {state,version} would have changed the on-disk shape — the adapter avoids that.)
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { findItemById } from '../utils/shopItems'

export const CART_STORAGE_KEY = 'shoppingCart'

/**
 * PersistStorage adapter that keeps the on-disk value as the RAW cart map (the
 * historical Overmind shape) under CART_STORAGE_KEY, while presenting Zustand the
 * `{ state: { cart }, version }` envelope it expects.
 * @type {import('zustand/middleware').PersistStorage<{ cart: Record<string, number> }>}
 */
const rawCartStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null
    const raw = window.localStorage.getItem(name)
    if (raw == null) return null
    try {
      const cart = JSON.parse(raw)
      // Guard: must be a plain object map (not null/array/scalar).
      if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return null
      return { state: { cart }, version: 0 }
    } catch {
      return null
    }
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return
    const cart = (value && value.state && value.state.cart) || {}
    // Write the RAW map — byte-compatible with the old Overmind persistence.
    window.localStorage.setItem(name, JSON.stringify(cart))
  },
  removeItem: (name) => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(name)
  },
}

export const useCartStore = create(
  persist(
    (set, get) => ({
      /** @type {Record<string, number>} the {itemId: quantity} cart map */
      cart: {},
      /** true once rehydrate() has run (post-mount) — SSR/first-render safe gate */
      _hasHydrated: false,
      setHasHydrated: (v) => set({ _hasHydrated: !!v }),

      // ---- actions (mirror the former Overmind cart operators EXACTLY) ----

      /**
       * Add each id to the cart. Mirrors operators.addItemToShoppingCart:
       *   - served=false buy-block → no-op, returns { ids: [] }.
       *   - increment quantity (start at 1); clamp back to 1 when the item is
       *     found in `shopItems` and is NOT isAllowedMultipleQuantitiesInCart.
       *     (An item NOT found in shopItems is left un-clamped, exactly as before.)
       * Returns the ids the caller should feed the AddToCart pixel (original ids
       * when served, [] when blocked) — matching the operator's return payload.
       * @param {string[]} ids
       * @param {{ served?: boolean, shopItems?: any[] }} [ctx]
       * @returns {{ ids: string[] }}
       */
      add: (ids, ctx = {}) => {
        const { served = true, shopItems = [] } = ctx
        if (!served) return { ids: [] }
        set((s) => {
          const cart = { ...s.cart }
          ids.forEach((id) => {
            cart[id] = cart[id] ? cart[id] + 1 : 1
            const item = findItemById(id, shopItems)
            if (cart[id] > 1 && !!item && !item.isAllowedMultipleQuantitiesInCart) {
              cart[id] = 1
            }
          })
          return { cart }
        })
        return { ids }
      },

      /**
       * Decrease quantity of one id. Mirrors operators.decreaseCartItemQuantity:
       * floor at 0 and delete the key when it reaches <= 0.
       * @param {string} id
       */
      decrease: (id) => set((s) => {
        const cart = { ...s.cart }
        cart[id] = cart[id] ? cart[id] - 1 : 0
        if (cart[id] <= 0) delete cart[id]
        return { cart }
      }),

      /**
       * Remove an id entirely. Mirrors operators.removeShoppingCartItem.
       * @param {string} id
       */
      remove: (id) => set((s) => {
        const cart = { ...s.cart }
        delete cart[id]
        return { cart }
      }),

      /** Empty the cart. Mirrors operators.resetShoppingCart. */
      reset: () => set({ cart: {} }),

      /**
       * Prune ids that DO NOT EXIST in the current region's catalog. Mirrors
       * operators.removeUnavailableShoppingCartItems: items that exist but are
       * unavailable-in-region (no price) are KEPT (shown greyed out); only ids
       * absent from `shopItems` entirely are dropped.
       * @param {any[]} shopItems
       */
      pruneMissing: (shopItems) => set((s) => {
        const allIds = (shopItems || []).map((i) => i.id)
        const cart = { ...s.cart }
        Object.keys(cart)
          .filter((id) => !allIds.includes(id))
          .forEach((id) => { if (cart[id] > 0) delete cart[id] })
        return { cart }
      }),
    }),
    {
      name: CART_STORAGE_KEY,
      storage: rawCartStorage,
      // Persist ONLY the cart line items — never the transient hydration flag.
      partialize: (s) => ({ cart: s.cart }),
      // SSR-safe: do NOT hydrate at module load; <CartStoreHydrator/> triggers it
      // post-mount so SSR === first client render (empty cart).
      skipHydration: true,
      version: 0,
      onRehydrateStorage: () => (state) => {
        if (state) state.setHasHydrated(true)
      },
    }
  )
)

// ---- selectors (import-and-use with useCartStore(selector)) ----

/** @param {{ cart: Record<string, number> }} s */
export const selectCart = (s) => s.cart
/** Total item count (sum of quantities). @param {{ cart: Record<string, number> }} s */
export const selectCartCount = (s) =>
  Object.keys(s.cart).reduce((acc, id) => acc + s.cart[id], 0)
